/**
 * Renders the agent's `show_chart` output as a chart, so trends and comparisons
 * from query results reach the user as a picture instead of rows to scan.
 *
 * The dashboard charts are sized for 800-pixel cards and render too small in the
 * chat panel, so this draws a chat-sized chart that scales with the panel. The
 * payload is server-built from query results, so only a completed, well-formed
 * output renders; anything else shows a short error.
 */
import React from 'react';
import { Legend, LegendProvider, useVictoryTheme, victoryBarPlugin, victoryLinePlugin, victoryTooltipPlugin } from '@amzn/meridian';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { colorBlue500, colorOrange400, colorPurple400, colorTeal500 } from '@amzn/meridian-tokens/base/color';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLine, VictoryScatter, VictoryVoronoiContainer } from 'victory';
import { useTranslation } from 'src/arb-wrapper';
import { formatCompactNumber } from 'src/utils/formatCompactNumber';
import { type ChatMessageProps } from './types';
import { formatTableCell, isTableCell, isTableColumn, NUMERIC_FORMATS, type TableCellValue, type TableColumn } from './visualCells';

interface ChartOutput {
  title: string;
  chartType: 'bar' | 'line';
  caption?: string;
  columns: TableColumn[];
  rows: TableCellValue[][];
  truncated?: boolean;
}

interface ChartDatum { x: string | number; y: number | null; seriesKey: string }

interface ChartSeries { key: string; label: string; color: string; column: TableColumn; data: ChartDatum[] }

interface LegendRenderProps {
  getBarProps?: (key: string) => Record<string, unknown>;
  getLineProps?: (key: string) => Record<string, unknown>;
  getScatterProps?: (key: string) => Record<string, unknown>;
  voronoiContainerProps: Record<string, unknown>;
  Tooltip: React.ComponentType<{ legendKey: string }>;
}

const CHART_WIDTH = 400;
const CHART_HEIGHT = 240;
const CHART_PADDING = { bottom: 56, left: 56, right: 16, top: 16 };
const PLOT_WIDTH = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
const MAX_X_TICKS = 6;
const MAX_TICK_LABEL_LENGTH = 12;
const MIN_COLUMNS = 2;
const MAX_COLUMNS = 5;
const SERIES_COLORS = [colorBlue500, colorOrange400, colorTeal500, colorPurple400];
const TICK_LABEL_STYLE = { fontSize: 10 };

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isChartRow = (row: unknown, columnCount: number): boolean => {
  if (!Array.isArray(row) || row.length !== columnCount) return false;
  const [x, ...values] = row as unknown[];
  return isTableCell(x) && values.every((value) => value === null || isFiniteNumber(value));
};

const isChartOutput = (value: unknown): value is ChartOutput => {
  const chart = value as Partial<ChartOutput> | null;
  if (typeof chart?.title !== 'string' || chart.title.trim() === '') return false;
  if (chart.chartType !== 'bar' && chart.chartType !== 'line') return false;
  if (chart.caption != null && typeof chart.caption !== 'string') return false;
  if (chart.truncated != null && typeof chart.truncated !== 'boolean') return false;
  if (!Array.isArray(chart.columns) || !Array.isArray(chart.rows)) return false;
  const columns: unknown[] = chart.columns;
  if (columns.length < MIN_COLUMNS || columns.length > MAX_COLUMNS || !columns.every(isTableColumn)) return false;
  if (new Set(columns.map(({ key }) => key)).size !== columns.length) return false;
  if (!columns.slice(1).every(({ format }) => NUMERIC_FORMATS.has(format))) return false;
  return chart.rows.every((row: unknown) => isChartRow(row, columns.length));
};

// A point without an x value has no place on the axis, so its row is skipped.
const buildSeries = ({ chartType, columns, rows }: ChartOutput): ChartSeries[] => {
  const plottedRows = rows.filter((row): row is [string | number, ...TableCellValue[]] => row[0] !== null);
  return columns.slice(1).map((column, index) => ({
    color: SERIES_COLORS[index],
    column,
    data: plottedRows
      .map((row) => ({ seriesKey: column.key, x: row[0], y: row[index + 1] as number | null }))
      .filter(({ y }) => chartType === 'line' || y !== null),
    key: column.key,
    label: column.label,
  }));
};

const sampleTickValues = (values: ReadonlyArray<string | number>): Array<string | number> => {
  if (values.length <= MAX_X_TICKS) return [...values];
  const step = (values.length - 1) / (MAX_X_TICKS - 1);
  return Array.from({ length: MAX_X_TICKS }, (_, index) => values[Math.round(index * step)]);
};

const shortenLabel = (label: string): string =>
  label.length > MAX_TICK_LABEL_LENGTH ? `${label.slice(0, MAX_TICK_LABEL_LENGTH - 1)}…` : label;

const getSharedCurrency = (series: readonly ChartSeries[]): string | undefined => {
  const currencies = new Set(series.map(({ column }) => (column.format === 'currency' ? column.currency : undefined)));
  const [currency] = Array.from(currencies);
  return currencies.size === 1 ? currency : undefined;
};

// Victory drops line datasets shorter than two points, so a lone value is drawn as a marker instead.
const getLonePoint = (data: readonly ChartDatum[]): ChartDatum | undefined => {
  const points = data.filter(({ y }) => y !== null);
  return points.length === 1 ? points[0] : undefined;
};

const getBarWidth = (categoryCount: number, seriesCount: number): number =>
  Math.max(2, Math.min(16, Math.floor((PLOT_WIDTH * 0.8) / Math.max(1, categoryCount * seriesCount))));

interface ChatChartPlotProps { readonly chart: ChartOutput; readonly series: readonly ChartSeries[] }

const ChatChartPlot: React.FC<ChatChartPlotProps> = ({ chart, series }) => {
  const theme = useVictoryTheme({ showIndependentGrid: false });
  const isBar = chart.chartType === 'bar';
  const xValues = Array.from(new Set(chart.rows.map(([x]) => x).filter((x): x is string | number => x !== null)));
  const currency = getSharedCurrency(series);
  const barWidth = getBarWidth(xValues.length, series.length);
  const formatDatum = ({ datum }: { datum: ChartDatum }): string => {
    const column = series.find(({ key }) => key === datum.seriesKey)?.column;
    return column ? formatTableCell(datum.y, column) : '';
  };
  return (
    <LegendProvider
      aria-label={chart.title}
      data={series.map(({ color, key, label }) => ({ color, key, label }))}
      plugins={[(isBar ? victoryBarPlugin : victoryLinePlugin)({ theme }), victoryTooltipPlugin()]}
    >
      {({ getBarProps, getLineProps, getScatterProps, voronoiContainerProps, Tooltip }: LegendRenderProps) => (
        <Column spacing="200" width="100%">
          <VictoryChart
            containerComponent={<VictoryVoronoiContainer {...voronoiContainerProps} labels={formatDatum} responsive={true} voronoiDimension="x" />}
            domainPadding={{ x: isBar ? (barWidth * series.length) / 2 + 8 : 8 }}
            height={CHART_HEIGHT}
            padding={CHART_PADDING}
            theme={theme}
            width={CHART_WIDTH}
          >
            <VictoryAxis
              style={{ tickLabels: { ...TICK_LABEL_STYLE, angle: -30, textAnchor: 'end' } }}
              tickFormat={(value: string | number) => shortenLabel(formatTableCell(value, chart.columns[0]))}
              tickValues={sampleTickValues(xValues)}
            />
            <VictoryAxis
              dependentAxis
              style={{ tickLabels: TICK_LABEL_STYLE }}
              tickFormat={(value: number) => formatCompactNumber(value, currency)}
            />
            {isBar
              ? (
              <VictoryGroup offset={barWidth}>
                {series.map(({ data, key }) => (
                  <VictoryBar
                    barWidth={barWidth}
                    data={data}
                    key={key}
                    labelComponent={<Tooltip legendKey={key} />}
                    labels={formatDatum}
                    {...getBarProps?.(key)}
                  />
                ))}
              </VictoryGroup>
                )
              : series.map(({ data, key }) => {
                const lonePoint = getLonePoint(data);
                return lonePoint
                  ? (
                  <VictoryScatter
                    data={[lonePoint]}
                    key={key}
                    labelComponent={<Tooltip legendKey={key} />}
                    labels={formatDatum}
                    {...getScatterProps?.(key)}
                  />
                    )
                  : (
                  <VictoryLine
                    data={data}
                    key={key}
                    labelComponent={<Tooltip legendKey={key} />}
                    labels={formatDatum}
                    {...getLineProps?.(key)}
                  />
                    );
              })}
          </VictoryChart>
          {series.length > 1 && <Legend direction="horizontal" />}
        </Column>
      )}
    </LegendProvider>
  );
};

export const ChartMessage: React.FC<ChatMessageProps> = ({ part, index }) => {
  const { t } = useTranslation();
  if (part.state !== 'output-available' && part.state !== 'output-error') return null;
  const chart = part.state === 'output-available' && isChartOutput(part.output) ? part.output : null;
  if (!chart) return <Text color="error" data-testid="show-chart-error" key={index} type="b100">{t('chat_chart_error')}</Text>;
  return (
    <Column key={index} spacing="200" width="100%">
      <Text type="h100">{chart.title}</Text>
      {chart.rows.length === 0
        ? <Text color="secondary" type="b200">{t('chart_no_data_available')}</Text>
        : (
        <div data-testid="chat-chart" style={{ width: '100%' }}>
          <ChatChartPlot chart={chart} series={buildSeries(chart)} />
        </div>
          )}
      {chart.caption && <Text color="secondary" type="b100">{chart.caption}</Text>}
      {chart.truncated && <Text color="secondary" type="b100">{t('chat_chart_truncated', { count: chart.rows.length })}</Text>}
    </Column>
  );
};
